/**
 * Global notification center (STEP 11) — a bell icon with an unread
 * badge, opening a panel listing durable notifications
 * (`store/notification.store.ts`). Lives in the app header
 * (`layouts/AppLayout/Header.tsx`).
 *
 * REBUILT OFF MUI (desk, 2026-09-29) — the last MUI holdout in the
 * notification family; `NotificationDialog` and `SystemStatusNotice`
 * already ran on `ui-tw`. `Popover` is Radix underneath (see
 * `ui-tw/overlay.tsx`'s own header), which is why this stays a corner
 * panel rather than a `Dialog` — a notification HISTORY is exactly the
 * kind of thing the "only error/warning centre" rule (`notify.ts`'s
 * `ACKNOWLEDGE`) says should not compete for the centre of the screen.
 */
import { useState, type JSX } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Popover, IconButton, Button, Divider, Stack, Text } from '@components/ui-tw';
import { cn } from '@utils/cn';
import { useNotificationStore } from '@store/notification.store';

dayjs.extend(relativeTime);

export function NotificationCenter(): JSX.Element {
  const [open, setOpen] = useState(false);
  const notifications = useNotificationStore((state) => state.notifications);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const markRead = useNotificationStore((state) => state.markRead);
  const markAllRead = useNotificationStore((state) => state.markAllRead);

  return (
    // The wrapper — not the trigger — anchors the count badge. Radix clones
    // its injected props (role, aria-expanded, onClick...) onto whatever
    // `trigger` renders, so that element must be the real `<button>`
    // (`IconButton`) or the panel opens from a `<span>` with no native
    // button semantics. The badge is a sibling positioned off THIS span
    // instead — MUI's `Badge` did its own anchoring; here the wrapper does.
    <span className="tw:relative tw:inline-flex">
      <Popover
        open={open}
        onOpenChange={setOpen}
        align="end"
        className="tw:w-[360px] tw:max-h-[480px] tw:overflow-y-auto"
        trigger={
          <IconButton icon="notifications" label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`} />
        }
      >
        <Stack direction="row" align="center" justify="between" className="tw:px-3 tw:py-2">
          <Text variant="title">Notifications</Text>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </Stack>
        <Divider />
        {notifications.length === 0 ? (
          <div className="tw:p-6 tw:text-center">
            <Text variant="body" tone="muted">
              No notifications yet
            </Text>
          </div>
        ) : (
          <ul className="tw:m-0 tw:list-none tw:p-0">
            {notifications.slice(0, 20).map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => markRead(notification.id)}
                  className={cn(
                    'tw:flex tw:w-full tw:flex-col tw:gap-0.5 tw:border-b tw:border-nx-border tw:px-3 tw:py-2 tw:text-left tw:transition-colors tw:duration-200 tw:last:border-b-0 tw:hover:bg-nx-surface-hover',
                    notification.read && 'tw:opacity-60',
                  )}
                >
                  <Text variant="body" as="span">
                    {notification.title}
                  </Text>
                  <Text variant="caption" tone="muted" as="span">
                    {notification.message}
                    {notification.message ? ' · ' : ''}
                    {dayjs(notification.createdAt).fromNow()}
                  </Text>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Popover>
      {unreadCount > 0 && (
        <span
          aria-hidden
          className="tw:pointer-events-none tw:absolute tw:-top-0.5 tw:-right-0.5 tw:flex tw:h-4 tw:min-w-4 tw:items-center tw:justify-center tw:rounded-full tw:bg-nx-error tw:px-1 tw:text-[10px] tw:font-semibold tw:text-nx-text-on-accent"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </span>
  );
}
